import { createReducer } from '@reduxjs/toolkit';
import { SiteDto, IReturnOrderDto, ReturnOrderDto } from '../../api/ClientApi';
import { myAssert } from '../../common/console';

import {
    createNewEmptyReturnOrder,
    createNewReturnOrder,
    clearReturnOrder,
    saveReturnOrder,
    importReturnOrder,
    removeReturnOrderOriginSite,
    setReturnOrderOriginSite,
    updateReturnOrderData,
    updateReturnOrderDescription,
} from '../actions/returnOrderActions';
import {
    clearBaseEntityState,
    compareModifiedDate,
    IBaseEntityState,
    IModifiedInfo,
    SaveEntityStatus,
    setBaseEntityCurrentJson,
} from './common';

export interface ReturnOrderFormReducerState extends IBaseEntityState {
    returnOrder: IReturnOrderDto | null;
}

function clearState(state: ReturnOrderFormReducerState): void {
    state.returnOrder = null;

    clearBaseEntityState(state);
}

function getInitialState(): ReturnOrderFormReducerState {
    const state: ReturnOrderFormReducerState = {
        returnOrder: null,
        baseJsonString: null,
        currentJsonString: null,
        isChanged: undefined,
        saveStatus: SaveEntityStatus.None,
        modified: undefined,
    };

    clearState(state);

    return state;
}

function calculateCurrentJson(state: ReturnOrderFormReducerState, setAsBaseline: boolean = false): void {
    myAssert(() => state.returnOrder != null, 'Need order to calculate');

    const jsonBaseline = JSON.stringify(state.returnOrder);

    setBaseEntityCurrentJson(state, jsonBaseline, setAsBaseline);
}

function calculateBaseInfo(state: ReturnOrderFormReducerState) {
    myAssert(() => state.returnOrder != null, 'Need order to calculate');

    if (!state.returnOrder!.id || state.returnOrder!.id <= 0) {
        // no modified data should be set
    } else {
        // lets find the last modified part of order
        const modified: IModifiedInfo | undefined = compareModifiedDate(state.returnOrder, undefined);

        state.modified =
            (modified ?? state.returnOrder!.created)
                ? {
                      modifiedAt: state.returnOrder!.created ?? new Date(),
                      modifiedBy: state.returnOrder!.createdBy ?? '',
                  }
                : undefined;
    }
}

export const returnOrderFormReducer = createReducer<ReturnOrderFormReducerState>(
    getInitialState(), // Initial state
    (builder) => {
        builder
            .addCase(createNewEmptyReturnOrder, (state: ReturnOrderFormReducerState, action: any) => {
                clearState(state);

                const newOrder: IReturnOrderDto = new ReturnOrderDto();

                newOrder.id = 0;

                state.returnOrder = newOrder;

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(createNewReturnOrder, (state: ReturnOrderFormReducerState, action: any) => {
                clearState(state);

                const payload: IReturnOrderDto = action.payload;

                // check order data
                myAssert(() => payload.id === 0, 'It is new order!!!');

                state.returnOrder = payload;

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(importReturnOrder.fulfilled, (state: ReturnOrderFormReducerState, action: any) => {
                clearState(state);

                const payload: IReturnOrderDto = action.payload;

                myAssert(() => !!payload, 'Required Data for Import');

                state.returnOrder = payload;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(clearReturnOrder, (state: ReturnOrderFormReducerState, action: any) => {
                clearState(state);
            })
            .addCase(setReturnOrderOriginSite, (state: ReturnOrderFormReducerState, action: any) => {
                myAssert(() => state.returnOrder !== null, 'There needs to be order if it is being updated');

                const site = action.payload as SiteDto;

                state.returnOrder! = { ...state.returnOrder, originSite: site, originSiteId: site?.id };

                calculateCurrentJson(state);
            })
            .addCase(removeReturnOrderOriginSite, (state: ReturnOrderFormReducerState, action: any) => {
                myAssert(() => state.returnOrder !== null, 'There needs to be order if it is being updated');

                state.returnOrder! = { ...state.returnOrder, originSite: undefined, originSiteId: undefined };

                calculateCurrentJson(state);
            })
            .addCase(updateReturnOrderDescription, (state: ReturnOrderFormReducerState, action: any) => {
                myAssert(() => state.returnOrder !== null, 'There needs to be order if it is being updated');

                state.returnOrder! = { ...state.returnOrder, description: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateReturnOrderData, (state: ReturnOrderFormReducerState, action: any) => {
                myAssert(() => state.returnOrder !== null, 'There needs to be order if it is being updated');

                state.returnOrder! = { ...state.returnOrder, data: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(saveReturnOrder.pending, (state: ReturnOrderFormReducerState, action: any) => {
                myAssert(() => state.returnOrder !== null, 'There needs to be order if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(saveReturnOrder.fulfilled, (state: ReturnOrderFormReducerState, action: any) => {
                myAssert(() => state.returnOrder !== null, 'There needs to be order if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(saveReturnOrder.rejected, (state: ReturnOrderFormReducerState, action: any) => {
                myAssert(() => state.returnOrder !== null, 'There needs to be order if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            });
    },
);
